import { useSearchParams } from 'react-router-dom';
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import { Section } from '@/components/fleetguardian';
import { Skeleton } from '@/components/ui/skeleton';
import { useFraudHubByMake } from '@/api/hooks/useFraudHubByMake';
import {
  CHART_GRID_STROKE,
  chartAxis,
  chartTooltipItemStyle,
  chartTooltipLabelStyle,
  chartTooltipStyle,
  seriesColor,
} from '@/lib/chartTheme';
import { fmtNum } from '@/components/fleetguardian/format';
import { parsePeriod } from '@/lib/timePeriod';

/**
 * Fraud dashboard "by make" breakdown (Wave 2C) — a horizontal bar of fraud
 * alert_count per vehicle make from the NEW `/fraud-hub/by-make` endpoint
 * (analytics.fraud_alert_daily_by_dim). Server-ranked by alert_count desc.
 */
export function FraudByMakeChart(): JSX.Element {
  const [searchParams] = useSearchParams();
  const period = parsePeriod(searchParams.get('period'), 'M');
  const { data, isLoading, isError, error } = useFraudHubByMake(period, 12);

  const label = data?.period_label ? ` — ${data.period_label}` : '';
  const items = data?.items ?? [];
  const hasData = items.some((i) => i.value > 0);
  const height = Math.max(200, items.length * 28 + 48);

  return (
    <Section title={`Alerts by Vehicle Make${label}`}>
      {isLoading && <Skeleton className="h-[280px] w-full" />}
      {!isLoading && isError && (
        <div className="rounded-md border border-destructive/40 bg-destructive/5 p-4 text-sm text-destructive">
          Failed to load by-make breakdown: {error?.message ?? 'unknown error'}
        </div>
      )}
      {!isLoading && !isError && !hasData && (
        <div className="flex h-[240px] items-center justify-center text-sm text-muted-foreground">
          No alerts attributed to a make in this window.
        </div>
      )}
      {!isLoading && !isError && hasData && (
        <div style={{ height }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              layout="vertical"
              data={items}
              margin={{ top: 4, right: 16, bottom: 4, left: 8 }}
            >
              <CartesianGrid horizontal={false} stroke={CHART_GRID_STROKE} />
              <XAxis type="number" {...chartAxis} allowDecimals={false} />
              <YAxis
                type="category"
                dataKey="key"
                {...chartAxis}
                width={110}
                tick={{ ...chartAxis.tick, fontSize: 11 }}
              />
              <Tooltip
                cursor={{ fill: 'rgba(31,107,117,0.06)' }}
                contentStyle={chartTooltipStyle}
                itemStyle={chartTooltipItemStyle}
                labelStyle={chartTooltipLabelStyle}
                formatter={(value: number) => [fmtNum(value), 'Alerts']}
              />
              <Bar
                dataKey="value"
                name="Alerts"
                fill={seriesColor(0)}
                radius={[0, 4, 4, 0]}
                isAnimationActive={false}
              />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
    </Section>
  );
}

export default FraudByMakeChart;
